import { useMemo } from 'react';

import { competencyTransform } from '@backrs_ui/common-ui';

import studyHabitsBanner from '@backrs_ui/grow-ui/src/assets/images/study-habits-guide/study-habits-banner.png';
import {
  GuideAccordionList,
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useGetContentNuggetsBySlugsQuery } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { HabitSection, WorkdayRulesSection } from './components';
import { useAgentContext } from './StudyHabitsGuidePageContainer.agentContext';
import { studyHabitsContent } from '../content';

const SECTION_IDS = {
  overview: 'study-habits-guide-section-overview',
  tools: 'study-habits-guide-section-tools',
  habits: 'study-habits-guide-section-habits',
  workday: 'study-habits-guide-section-workday',
} as const;

const STUDY_HABITS_GUIDE_TOOL_ID = 'study_habits_guide';

const STUDY_HABITS_TOOL_IDS: readonly StickerCardId[] = ['improve_test_scores'];

const STUDY_HABITS_COMPETENCY_SLUGS: readonly string[] = [
  'academics-studying',
  'foundational-learning',
];

const STUDY_HABITS_NUGGET_SLUGS: readonly string[] = [
  'retrieval-practice',
  'the-9-best-scientific-study-tips',
  'study-effectively-',
  'how-to-plan-for-exams-and-set-up-an-effective-schedule',
];

export const StudyHabitsGuidePageContainer = () => {
  useAgentContext();

  const { data: resourceNuggets } = useGetContentNuggetsBySlugsQuery(STUDY_HABITS_NUGGET_SLUGS);
  const { contentCompetencies } = useAppSelector(selectContentCompetencies);

  const competencyNuggets = useMemo(() => {
    const bySlug = new Map(contentCompetencies.map((c) => [c.slug, c]));

    return STUDY_HABITS_COMPETENCY_SLUGS.map((slug) => bySlug.get(slug))
      .filter((c): c is NonNullable<typeof c> => Boolean(c))
      .map(competencyTransform);
  }, [contentCompetencies]);

  const orderedNuggets = useMemo(() => {
    if (!resourceNuggets) return [];

    const bySlug = new Map(resourceNuggets.map((n) => [n.slug, n]));

    return STUDY_HABITS_NUGGET_SLUGS.map((slug) => bySlug.get(slug)).filter(
      (n): n is NonNullable<typeof n> => Boolean(n),
    );
  }, [resourceNuggets]);

  const toolResourceNuggets = useMemo(
    () => [...competencyNuggets, ...orderedNuggets],
    [competencyNuggets, orderedNuggets],
  );

  const habitItems = studyHabitsContent.habits.map((habit) => ({
    id: habit.id,
    title: `${habit.number}. ${habit.title}`,
    details: <HabitSection content={habit} />,
  }));

  const sections = [
    {
      id: SECTION_IDS.habits,
      title: studyHabitsContent.habitsSection.title,
      subtitle: studyHabitsContent.habitsSection.subtitle,
      rightSlot: (
        <LubavButton
          id="study-habits-guide-habits-lubav-btn"
          label="Apply this to me"
          prompt="Based on what you know about me, help me apply these five study habits to my own classes and schedule."
          allowTools={false}
        />
      ),
      content: <GuideAccordionList idPrefix="study-habits-guide-habit" items={habitItems} />,
    },
    {
      id: SECTION_IDS.tools,
      title: studyHabitsContent.tools.title,
      subtitle: studyHabitsContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="study-habits-guide-tools-lubav-btn"
          label="Where should I start?"
          prompt="Which of these tools should I start with to build better study habits?"
          allowTools={false}
        />
      ),
      content: (
        <GuideToolsSection toolIds={STUDY_HABITS_TOOL_IDS} extraNuggets={toolResourceNuggets} />
      ),
    },
    {
      id: SECTION_IDS.workday,
      title: studyHabitsContent.workday.title,
      subtitle: studyHabitsContent.workday.subtitle,
      rightSlot: (
        <LubavButton
          id="study-habits-guide-workday-lubav-btn"
          label="Design my workday"
          promptButtonId="study_habits_design_workday"
        />
      ),
      content: <WorkdayRulesSection content={studyHabitsContent.workday} />,
    },
  ];

  return (
    <GuidePageShell
      toolId={STUDY_HABITS_GUIDE_TOOL_ID}
      banner={{
        src: studyHabitsBanner,
        alt: "The High School Student's Guide to Studying Smarter (Not Harder)",
      }}
    >
      <GuideSections
        header={studyHabitsContent.header}
        overview={{ id: SECTION_IDS.overview }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
